import { useMemo } from 'react';
import {
  getPetCremation,
  getPlanAssistances,
  getPlanBeneficiary,
  getPlanChapel,
  getPlanCremation,
  getPlanFuneral,
  getPlanInsurances,
  getPlanLifeBenefits,
  getPlanRules,
} from './plan-details.client';
import type { Params } from './plan-details.client';
import {
  DependentRuleGroupType,
  DependentRuleType,
  ExtendedGroupType,
  KinshipGroupType,
  SubscriptionDependentRuleType,
} from './dependents-rule/rules.types';
import {
  subscriptionRuleForDependentWithoutExplicitRule,
  subscriptionRulesForAdditionalDependent,
} from './dependents-rule/constants';

export const usePlanBeneficiary = (params: Params) => {
  return useMemo(() => {
    return getPlanBeneficiary(params);
  }, [params.uf, params.slug]);
};

export const usePlanInsurances = (params: Params) => {
  const currentPlanInsurances = useMemo(() => {
    return getPlanInsurances(params);
  }, [params.slug, params.uf]);

  const planInsurances = useMemo(() => {
    return Object.values(currentPlanInsurances).filter(shouldIncludeAssistance);
  }, [currentPlanInsurances]);

  const insuranceAlert = {
    'bem-estar': `*Seguros garantidos por Prever Angelus Seguros S/A, CNPJ:
        18.133.809/0001-57, Registro SUSEP 03387. Processo SUSEP:
        15414.902197/2014-32.`,
    'bem-seguro': `*Seguros garantidos por Prever Angelus Seguros S/A, CNPJ:
        18.133.809/0001-57, Registro SUSEP 03387. Processo SUSEP:
        15414.902392/2019-77.`,
    multi: `*Seguros garantidos por Prever Angelus Seguros S/A, CNPJ:
        18.133.809/0001-57, Registro SUSEP 03387. Processos SUSEP:
        15414.902197/2014-32, 15414.902392/2019-77 e 15414.900615/2019-61.`,
  };

  return { planInsurances, insuranceAlert };
};

export const usePlanAssistance = (params: Params) => {
  const currentPlanAssistance = useMemo(() => {
    return getPlanAssistances(params);
  }, [params.uf, params.slug]);

  const planAssistances = useMemo(() => {
    return Object.values(currentPlanAssistance).filter(shouldIncludeAssistance);
  }, [currentPlanAssistance]);

  return planAssistances;
};

export const usePlanLifeBenefits = (params: Params) => {
  return useMemo(() => {
    return getPlanLifeBenefits(params);
  }, [params.uf, params.slug]);
};

export const usePlanRules = (params: Params) => {
  return useMemo(() => {
    return getPlanRules(params);
  }, [params.uf, params.slug]);
};

export const usePlanFuneral = (params: Params) => {
  return useMemo(() => {
    return getPlanFuneral(params);
  }, [params.uf, params.slug]);
};

export const usePlanChapel = (params: Params) => {
  return useMemo(() => {
    return getPlanChapel(params);
  }, [params.slug, params.uf]);
};

export const usePlanCremation = (params: Params) => {
  return useMemo(() => {
    return getPlanCremation(params);
  }, [params.slug, params.uf]);
};

export const usePetCremation = (params: Params) => {
  return useMemo(() => {
    return getPetCremation(params);
  }, [params.slug, params.uf]);
};

export const usePlanUrn = (params: Params) => {
  const currentPlanCremation = usePlanCremation(params);
  return useMemo(() => {
    return currentPlanCremation?.items.map((item) => {
      return `${item.title} ${item.description}`;
    });
  }, [currentPlanCremation]);
};

const shouldIncludeAssistance = (assistance) => assistance?.items.length > 0;

export const getAllGroupsDependentRule = (
  dependent_rule: DependentRuleType,
  subscription_dependent_rule: SubscriptionDependentRuleType[],
) => {
  if (!dependent_rule) return [];

  const hasAdditionalDependentGlobal = subscription_dependent_rule?.some(
    (rule) => rule?.is_additional_dependent,
  );

  const kinshipGroups: Record<string, KinshipGroupType> = {};

  dependent_rule?.specifics?.forEach((specific) => {
    const siblingGroups: Array<
      DependentRuleGroupType & {
        specific_id?: number;
        maxQuantity: number | null;
      }
    > = specific?.groups?.map((group) => ({
      ...group,
      maxQuantity: specific?.max_quantity ?? null,
    }));

    specific?.groups?.forEach((group) => {
      const kinshipSlug = group?.kinship?.slug;
      if (!kinshipSlug) return;

      if (!kinshipGroups[kinshipSlug]) {
        kinshipGroups[kinshipSlug] = {
          kinship: group.kinship,
          groups: [],
          maxQuantityAdditional:
            dependent_rule?.max_quantity_additional ?? null,
          hasAdditionalDependentGlobal,
        };
      }

      const kinshipGroup = kinshipGroups[kinshipSlug];
      const maritalStatus = group.marital_status ?? null;

      const rules = subscription_dependent_rule?.filter(
        (rule) => rule.kinship?.slug == kinshipSlug || rule.kinship === null,
      );

      const kinshipGroupValue = group.kinship?.slug;
      const minAgeGroupValue = group.min_age;
      const maxAgeGroupValue = group.max_age;
      const maritalStatusGroupValue = group.marital_status;
      const financialDependentGroupValue = group.is_financial_dependent;

      let bestRules = rules?.filter((rule) => {
        const kinshipValue = rule.kinship?.slug;
        const minAgeValue = rule.min_age;
        const maxAgeValue = rule.max_age;
        const maritalStatusValue = rule.marital_status;
        const financialDependentValue = rule.is_financial_dependent;

        const selectKinshipMatch = kinshipGroupValue
          ? kinshipGroupValue
            ? kinshipGroupValue == kinshipValue
            : true
          : true;

        const selectMaritalStatusMatch =
          maritalStatusGroupValue == null ||
          maritalStatusValue == null ||
          maritalStatusGroupValue == maritalStatusValue;

        const selectMinAgeMatch =
          minAgeGroupValue == null ||
          (minAgeValue != null && minAgeValue >= minAgeGroupValue);

        const selectMaxAgeMatch =
          maxAgeGroupValue == null ||
          (maxAgeValue != null && maxAgeValue <= maxAgeGroupValue);

        const selectFinancialDependentMatch =
          financialDependentGroupValue == null ||
          financialDependentGroupValue == financialDependentValue;

        return (
          selectKinshipMatch &&
          selectMaritalStatusMatch &&
          selectMinAgeMatch &&
          selectMaxAgeMatch &&
          selectFinancialDependentMatch
        );
      });

      let subscriptionRules =
        subscription_dependent_rule?.filter(
          (rule) =>
            rule?.kinship?.slug === kinshipSlug &&
            (rule?.marital_status ?? null) === maritalStatus,
        ) ?? [];

      if (subscriptionRules?.length === 0) {
        subscriptionRules = [subscriptionRuleForDependentWithoutExplicitRule()];
        bestRules = [subscriptionRuleForDependentWithoutExplicitRule()];
      }

      kinshipGroup?.groups?.push({
        ...group,
        specific_id: specific.id,
        maxQuantity: specific.max_quantity ?? null,
        subscriptionRules: bestRules,
        siblingGroup: siblingGroups,
      });
    });
  });

  const result = Object.values(kinshipGroups);

  const additionalGroup = getSubscriptionRulesForAdditionalDependent(
    dependent_rule,
    subscription_dependent_rule,
  );

  if (additionalGroup) {
    result.push(additionalGroup);
  }

  return Object.values(result);
};

export const getSubscriptionRulesForAdditionalDependent = (
  dependent_rule: DependentRuleType,
  subscription_dependent_rule: SubscriptionDependentRuleType[],
): KinshipGroupType | null => {
  const additionalSubscriptionRules =
    subscription_dependent_rule?.filter(
      (rule) => rule?.is_additional_dependent === true,
    ) ?? [];

  if (additionalSubscriptionRules?.length === 0) return null;

  return subscriptionRulesForAdditionalDependent(
    additionalSubscriptionRules,
    dependent_rule?.max_quantity_additional ?? null,
  );
};

export const getCurrentKinshipInFirstPosition = (
  siblings: ExtendedGroupType[],
  mainKinshipName: string,
): ExtendedGroupType[] => {
  return [
    ...siblings.filter(
      (dependent) => dependent.kinship?.slug === mainKinshipName,
    ),
    ...siblings.filter(
      (dependent) => dependent.kinship?.slug !== mainKinshipName,
    ),
  ];
};
